import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw, FiCheck, FiHelpCircle, FiGrid, FiEye, FiInfo } = FiIcons;

function MultiplicationSudokuGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  
  const [difficulty, setDifficulty] = useState('easy');
  const [grid, setGrid] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [hintMessage, setHintMessage] = useState(null);
  const [lastCheckStats, setLastCheckStats] = useState(null); // { correct: 0, wrong: 0, empty: 0 }
  
  const [mistakes, setMistakes] = useState(0);

  const generateGrid = (diff) => {
    const newGrid = [];
    const fillProb = diff === 'easy' ? 0.7 : diff === 'medium' ? 0.5 : 0.3;

    for (let r = 1; r <= 10; r++) {
      const row = [];
      for (let c = 1; c <= 10; c++) {
        const product = r * c;
        const isPreFilled = Math.random() < fillProb;
        row.push({
          row: r,
          col: c,
          correctValue: product,
          value: isPreFilled ? product : '',
          isPreFilled,
          isHinted: false,
          status: isPreFilled ? 'correct' : 'empty'
        });
      }
      newGrid.push(row);
    }
    return newGrid;
  };

  useEffect(() => {
    setGrid(generateGrid(difficulty));
    setGameCompleted(false);
    setShowCelebration(false);
    setMistakes(0);
    setLastCheckStats(null);
  }, [difficulty]);

  const handleInputChange = (rIndex, cIndex, val) => {
    if (gameCompleted) return;

    const newGrid = [...grid];
    const cell = { ...newGrid[rIndex][cIndex] };
    const numVal = val === '' ? '' : parseInt(val);
    
    cell.value = numVal;
    
    // Reset status to empty when user starts typing again
    // This allows the "Check All" colors to clear on interaction
    cell.status = val === '' ? 'empty' : 'pending';

    newGrid[rIndex][cIndex] = cell;
    setGrid(newGrid);
  };

  const checkAll = () => {
    let correctCount = 0;
    let wrongCount = 0;
    let emptyCount = 0;
    
    const newGrid = grid.map(row => 
      row.map(cell => {
        if (cell.isPreFilled) {
          correctCount++;
          return cell;
        }

        if (cell.value === '') {
          emptyCount++;
          return { ...cell, status: 'empty' };
        }

        if (parseInt(cell.value) === cell.correctValue) {
          correctCount++;
          return { ...cell, status: 'correct' };
        } else {
          wrongCount++;
          setMistakes(prev => prev + 1);
          return { ...cell, status: 'wrong' };
        }
      })
    );
    
    setGrid(newGrid);
    setLastCheckStats({ correct: correctCount, wrong: wrongCount, empty: emptyCount });

    if (wrongCount === 0 && emptyCount === 0) {
      handleGameComplete();
    } else if (emptyCount > 0) {
      setHintMessage(`Keep going! ${emptyCount} boxes still empty.`);
      setTimeout(() => setHintMessage(null), 3000);
    } else {
      setHintMessage(`Found ${wrongCount} errors. Check the red boxes!`);
      dispatch({ type: 'ADD_WRONG_ANSWER' });
      setTimeout(() => setHintMessage(null), 3000);
    }
  };

  const handleGameComplete = () => {
    setGameCompleted(true);
    setShowCelebration(true);
    
    const difficultyBonus = difficulty === 'hard' ? 50 : difficulty === 'medium' ? 30 : 10;
    const accuracyBonus = Math.max(0, 20 - mistakes);
    const totalBonus = difficultyBonus + accuracyBonus;

    dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'multiplication-sudoku', bonusTokens: totalBonus } });
    dispatch({ type: 'ADD_SCORE', payload: totalBonus });
  };

  const showHint = () => {
    if (!selectedCell) {
      setHintMessage("Click a box first!");
      setTimeout(() => setHintMessage(null), 2000);
      return;
    }

    const rIndex = selectedCell.row - 1;
    const cIndex = selectedCell.col - 1;
    const cell = grid[rIndex][cIndex];

    if (cell.isPreFilled || cell.status === 'correct') {
      setHintMessage("You already have this one!");
      setTimeout(() => setHintMessage(null), 1500);
      return;
    }

    const newGrid = [...grid];
    newGrid[rIndex][cIndex] = { ...cell, value: cell.correctValue, isHinted: true, status: 'correct' };
    setGrid(newGrid);
    setTimeout(() => {
      setGrid(prev => {
         const updated = [...prev];
         updated[rIndex][cIndex].isHinted = false;
         return updated;
      });
    }, 2000);
  };

  const restartGame = () => {
    setGrid(generateGrid(difficulty));
    setGameCompleted(false);
    setShowCelebration(false);
    setMistakes(0);
    setLastCheckStats(null);
  };

  return (
    <div className="min-h-screen p-4 pb-24 bg-gradient-to-br from-green-100 via-blue-50 to-purple-100 relative font-comic">
      {/* Celebration Overlay */}
      <AnimatePresence>
        {gameCompleted && showCelebration && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div 
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-green-400 text-center max-w-md w-full"
            >
              <div className="text-6xl mb-4">🏆</div>
              <h2 className="text-3xl font-kid text-green-700 mb-4">Sudoku Master!</h2>
              <p className="text-green-600 mb-6">
                You correctly filled all 100 boxes!
              </p>
              
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowCelebration(false)}
                  className="w-full bg-yellow-400 text-yellow-900 p-3 rounded-2xl font-kid text-lg shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiEye} /> Review My Progress
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={restartGame}
                  className="w-full bg-green-500 text-white p-3 rounded-2xl font-kid text-lg shadow-lg"
                >
                  New Game
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-white border-2 border-green-200 text-green-700 p-3 rounded-2xl font-kid text-lg"
                >
                  Home
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="bg-white p-2 rounded-xl shadow-md border-2 border-green-200 text-green-700 hover:bg-green-50 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-xl" />
            </button>
            <h1 className="text-2xl md:text-3xl font-kid text-green-800 flex items-center gap-2">
              <SafeIcon icon={FiGrid} /> Times Table Sudoku
            </h1>
          </div>

          <div className="flex bg-white p-1 rounded-xl shadow-sm border border-green-200">
            {['easy', 'medium', 'hard'].map(d => (
              <button
                key={d}
                onClick={() => setDifficulty(d)}
                disabled={gameCompleted}
                className={`px-4 py-1.5 rounded-lg font-comic capitalize transition-all ${
                  difficulty === d 
                    ? 'bg-green-500 text-white shadow-md' 
                    : 'text-gray-500 hover:bg-green-50'
                } ${gameCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {d}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            {!gameCompleted ? (
              <>
                <button 
                  onClick={showHint}
                  className="flex items-center gap-2 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-xl shadow-md font-kid hover:bg-yellow-500 transition-all"
                >
                  <SafeIcon icon={FiHelpCircle} /> Hint
                </button>
                <button 
                  onClick={checkAll}
                  className="flex items-center gap-2 bg-blue-500 text-white px-5 py-2 rounded-xl shadow-md font-kid hover:bg-blue-600 transition-all"
                >
                  <SafeIcon icon={FiCheck} /> Check Progress
                </button>
              </>
            ) : (
              <button 
                onClick={restartGame}
                className="flex items-center gap-2 bg-green-500 text-white px-5 py-2 rounded-xl shadow-md font-kid hover:bg-green-600 transition-all"
              >
                <SafeIcon icon={FiRefreshCw} /> New Game
              </button>
            )}
          </div>
        </div>

        {/* Progress Summary Bar */}
        {lastCheckStats && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 grid grid-cols-3 gap-4"
          >
            <div className="bg-green-100 border-2 border-green-200 p-3 rounded-2xl text-center">
              <p className="text-xs text-green-600 font-bold uppercase tracking-wider">Correct</p>
              <p className="text-2xl font-kid text-green-700">{lastCheckStats.correct} / 100</p>
            </div>
            <div className="bg-red-100 border-2 border-red-200 p-3 rounded-2xl text-center">
              <p className="text-xs text-red-600 font-bold uppercase tracking-wider">Errors</p>
              <p className="text-2xl font-kid text-red-700">{lastCheckStats.wrong}</p>
            </div>
            <div className="bg-blue-100 border-2 border-blue-200 p-3 rounded-2xl text-center">
              <p className="text-xs text-blue-600 font-bold uppercase tracking-wider">Remaining</p>
              <p className="text-2xl font-kid text-blue-700">{lastCheckStats.empty}</p>
            </div>
          </motion.div>
        )}

        {/* Grid Container */}
        <div className="bg-white rounded-[2rem] p-4 md:p-8 shadow-2xl border-4 border-green-200 overflow-x-auto">
          <table className="w-full border-collapse min-w-[750px]">
            <thead>
              <tr>
                <th className="p-2 w-12 h-12">
                  <div className="bg-purple-100 rounded-lg w-full h-full flex items-center justify-center text-purple-600">
                    ×
                  </div>
                </th>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <th key={num} className={`p-2 font-kid text-blue-600 text-xl border-b-2 border-blue-50 ${selectedCell?.col === num ? 'bg-blue-50 rounded-t-xl' : ''}`}>
                    {num}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((row, rIndex) => (
                <tr key={rIndex}>
                  <th className={`p-2 font-kid text-purple-600 text-xl border-r-2 border-purple-50 ${selectedCell?.row === rIndex + 1 ? 'bg-purple-50 rounded-l-xl' : ''}`}>
                    {rIndex + 1}
                  </th>
                  {row.map((cell, cIndex) => (
                    <td key={cIndex} className="p-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={cell.value}
                        readOnly={cell.isPreFilled || gameCompleted}
                        onFocus={() => setSelectedCell({ row: cell.row, col: cell.col })}
                        onChange={(e) => handleInputChange(rIndex, cIndex, e.target.value)}
                        className={`
                          w-full h-12 md:h-16 text-center text-xl md:text-2xl font-comic rounded-xl border-2 transition-all duration-300 outline-none
                          ${cell.isPreFilled 
                            ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-default' 
                            : cell.status === 'correct'
                              ? 'bg-green-100 text-green-700 border-green-500 font-bold shadow-inner'
                              : cell.status === 'wrong'
                                ? 'bg-red-100 text-red-600 border-red-500 shadow-md animate-shake'
                                : cell.status === 'pending'
                                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                                  : 'bg-white border-gray-200 focus:border-blue-400 focus:ring-4 focus:ring-blue-100'
                          }
                          ${cell.isHinted ? 'scale-110 ring-4 ring-yellow-300 z-10' : ''}
                        `}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded"></div>
            <span>Pre-filled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 border border-green-500 rounded"></div>
            <span>Correct Answer</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-100 border border-red-500 rounded"></div>
            <span>Needs Correction</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></div>
            <span>New Answer</span>
          </div>
        </div>

        <AnimatePresence>
          {hintMessage && (
            <motion.div 
              initial={{ opacity: 0, y: 50 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: 50 }}
              className="fixed bottom-10 left-1/2 transform -translate-x-1/2 bg-white px-8 py-4 rounded-2xl shadow-2xl border-4 border-blue-400 z-50 flex items-center gap-3"
            >
              <SafeIcon icon={FiInfo} className="text-blue-500 text-2xl" />
              <span className="font-kid text-blue-800 text-lg">{hintMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default MultiplicationSudokuGame;